<template>
  <div class="detail-page">
    <!-- 헤더 -->
    <header class="detail-header">
      <button class="header-btn" @click="goBack">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <h1 class="header-title">{{ property.name || '현장 상세' }}</h1>
      <div class="header-actions">
        <button class="header-btn" @click="shareProperty">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
          </svg>
        </button>
        <button class="header-btn" @click="handleToggleFavorite">
          <svg
            class="w-6 h-6"
            :class="isFavorited ? 'text-red-500' : ''"
            :fill="isFavorited ? 'currentColor' : 'none'"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
          </svg>
        </button>
      </div>
    </header>

    <!-- 로딩 -->
    <div v-if="loading" class="state-box">
      <div class="spinner"></div>
      <p>불러오는 중...</p>
    </div>

    <!-- 에러: 캠페인 없음 -->
    <div v-else-if="!campaign" class="state-box">
      <div class="error-icon">
        <svg class="w-16 h-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <h2 class="error-title">페이지를 찾을 수 없습니다</h2>
      <p class="error-desc">유효하지 않은 링크이거나 삭제된 캠페인입니다.</p>
      <router-link to="/" class="home-btn">홈으로 이동</router-link>
    </div>

    <!-- 에러: 만료됨 -->
    <div v-else-if="campaign.status === 'expired'" class="state-box">
      <div class="error-icon expired">
        <svg class="w-16 h-16 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <h2 class="error-title">캠페인이 종료되었습니다</h2>
      <p class="error-desc">이 캠페인의 유효 기간이 만료되었습니다.</p>
      <router-link to="/" class="home-btn">현장 둘러보기</router-link>
    </div>

    <!-- 컨텐츠 -->
    <div v-else class="content">
      <!-- 캠페인 배지 바 -->
      <div class="campaign-badge-bar">
        <span class="campaign-badge">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          전용 상담 링크
        </span>
        <span class="partner-badge">{{ campaign.partnerDisplayName }} 상담사</span>
      </div>

      <!-- 요약 정보 -->
      <div class="summary-bar">
        <p class="summary-address">{{ property.address }}</p>
        <div class="summary-info">
          <span v-if="property.propertyType">{{ getPropertyTypeLabel(property.propertyType) }}</span>
          <span v-if="property.totalUnits" class="divider">|</span>
          <span v-if="property.totalUnits">{{ property.totalUnits?.toLocaleString() }}세대</span>
          <template v-if="property.themes && property.themes.length">
            <span class="divider">|</span>
            <span v-for="(t, idx) in property.themes.slice(0, 2)" :key="t">
              {{ getThemeLabel(t) }}<span v-if="idx < Math.min(property.themes.length, 2) - 1">, </span>
            </span>
            <span v-if="property.themes.length > 2">외 {{ property.themes.length - 2 }}개</span>
          </template>
        </div>
      </div>

      <!-- 이미지 -->
      <div class="image-area">
        <img v-if="property.thumbnailUrl" :src="property.thumbnailUrl" :alt="property.name" />
        <div v-else class="no-image">
          <svg class="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
        </div>
        <div class="badges">
          <span :class="['badge', `badge-${property.status}`]">{{ getStatusLabel(property.status) }}</span>
        </div>
      </div>

      <!-- 기본 정보 -->
      <div class="section">
        <h1 class="name">{{ property.name }}</h1>
        <p class="price-range" v-if="priceRange">
          <span class="range-value">{{ priceRange }}</span>
        </p>
      </div>

      <!-- 가격 정보 -->
      <div class="section price-section">
        <div class="price-row">
          <span class="label">초기자금</span>
          <span class="value primary">{{ property.initialFund || '-' }}</span>
        </div>
        <div class="price-row">
          <span class="label">중도금</span>
          <span class="value">{{ property.middlePayment || '-' }}</span>
        </div>
        <div class="price-row">
          <span class="label">입주예정</span>
          <span class="value">{{ formatMoveInDate(property.moveInDate) }}</span>
        </div>
      </div>

      <!-- 건물 정보 -->
      <div class="section">
        <h2 class="section-title">건물 정보</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">총 세대</span>
            <span class="info-value">{{ property.totalUnits?.toLocaleString() || '-' }}세대</span>
          </div>
          <div class="info-item">
            <span class="info-label">동 수</span>
            <span class="info-value">{{ property.totalBuildings || '-' }}개동</span>
          </div>
          <div class="info-item">
            <span class="info-label">최고층</span>
            <span class="info-value">{{ property.maxFloor || '-' }}층</span>
          </div>
          <div class="info-item">
            <span class="info-label">주차</span>
            <span class="info-value">세대당 {{ property.parkingPerUnit || '-' }}대</span>
          </div>
          <div class="info-item">
            <span class="info-label">용적률</span>
            <span class="info-value">{{ property.floorAreaRatio || '-' }}%</span>
          </div>
          <div class="info-item">
            <span class="info-label">건폐율</span>
            <span class="info-value">{{ property.buildingCoverageRatio || '-' }}%</span>
          </div>
          <div class="info-item">
            <span class="info-label">난방</span>
            <span class="info-value">{{ getHeatingLabel(property.heatingType) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">가스</span>
            <span class="info-value">{{ getGasLabel(property.gasType) }}</span>
          </div>
        </div>
      </div>

      <!-- 평형/타입 -->
      <div v-if="property.types && property.types.length" class="section type-section">
        <h2 class="section-title">평형/타입</h2>
        <div class="type-tabs">
          <button
            v-for="(type, idx) in property.types"
            :key="idx"
            class="type-tab"
            :class="{ active: selectedTypeIdx === idx }"
            @click="selectedTypeIdx = idx"
          >
            {{ type.name }}
          </button>
        </div>
        <div v-if="property.types[selectedTypeIdx]" class="type-detail-card">
          <div class="type-detail-header">
            <div class="type-detail-left">
              <span class="type-detail-name">{{ property.types[selectedTypeIdx].name }}</span>
              <span class="type-detail-pyeong">{{ property.types[selectedTypeIdx].pyeong }}평</span>
            </div>
            <span class="type-detail-price">{{ formatPrice(property.types[selectedTypeIdx].price) }}</span>
          </div>
          <div class="type-detail-info">
            <div class="info-item">
              <span class="info-label">공급면적</span>
              <span class="info-value">{{ property.types[selectedTypeIdx].supplyArea }}㎡</span>
            </div>
            <div v-if="property.types[selectedTypeIdx].buildings?.length" class="info-item">
              <span class="info-label">해당 동</span>
              <span class="info-value">{{ property.types[selectedTypeIdx].buildings.join(', ') }}동</span>
            </div>
          </div>
          <img
            v-if="property.types[selectedTypeIdx].imageUrl"
            :src="property.types[selectedTypeIdx].imageUrl"
            :alt="`${property.types[selectedTypeIdx].name} 평면도`"
            class="type-floorplan"
          />
        </div>
      </div>

      <!-- 위치 지도 -->
      <div class="section">
        <h2 class="section-title">위치</h2>
        <div class="map-box" ref="mapContainer"></div>
        <p class="map-addr">{{ property.address }}</p>
      </div>

      <!-- 담당 상담사 (캠페인 전용) -->
      <div class="section partner-section">
        <h2 class="section-title">담당 상담사</h2>
        <div class="partner-card-main">
          <div class="partner-avatar">
            {{ campaign.partnerDisplayName?.charAt(0) || '?' }}
          </div>
          <div class="partner-info">
            <h3 class="partner-name">{{ campaign.partnerDisplayName }}</h3>
            <p class="partner-intro">{{ partnerIntro }}</p>
          </div>
        </div>
      </div>

      <!-- 여백 -->
      <div class="bottom-space"></div>
    </div>

    <!-- 하단 고정 버튼 -->
    <div v-if="!loading && campaign && campaign.status === 'active'" class="bottom-bar">
      <div class="bottom-price">
        <span class="price-main">{{ property.initialFund || '가격문의' }}</span>
        <span v-if="property.initialFund" class="price-sub">부터</span>
      </div>
      <button class="cta-btn" @click="openConsultationForm">
        상담 신청하기
      </button>
    </div>

    <!-- 상담 신청 시트 -->
    <ConsultationRequestSheet
      v-if="showConsultationForm"
      :property="property"
      :partner="campaignPartner"
      :isCampaign="true"
      :campaignId="campaign?.id"
      :campaignCode="campaign?.code"
      :adSource="adSource"
      @close="closeConsultationForm"
      @submit="handleSubmitConsultation"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { doc, getDoc, collection, query, where, getDocs, addDoc } from 'firebase/firestore'
import { db, auth } from '@/firebase/firebase'
import { useFavorite } from '@/composables/main/useFavorite'
import { useRecent } from '@/composables/main/useRecent'
import { useCampaign } from '@/composables/partners/useCampaign'
import ConsultationRequestSheet from '@/components/main/property/ConsultationRequestSheet.vue'

export default {
  name: 'CampaignLandingView',
  components: { ConsultationRequestSheet },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { isFavorite, toggleFavorite, loadFavorites } = useFavorite()
    const { addRecent } = useRecent()
    const { getCampaignByCode, incrementViews, incrementConsultations } = useCampaign()

    const loading = ref(true)
    const campaign = ref(null)
    const property = ref({})
    const partnerIntro = ref('정성껏 상담해드리겠습니다.')
    const isFavorited = ref(false)
    const mapContainer = ref(null)
    const selectedTypeIdx = ref(0)
    const showConsultationForm = ref(false)

    // 광고 유입 경로 (?from=recommend, ?from=adbanner 등)
    const adSource = ref(null)
    const validAdSources = ['banner', 'adbanner', 'popup', 'splash', 'recommend', 'community', 'chatad']
    if (route.query.from && validAdSources.includes(route.query.from)) {
      adSource.value = route.query.from
    }

    const themeLabels = {
      money: '실투자 3천만', brand: '1군 브랜드', subway: '초역세권',
      safe: '안심보장제', school: '명문학세권', forest: '힐링숲세권',
      job: '직주근접', mall: '몰세권'
    }

    // 캠페인 파트너 정보 (상담 신청 시트에 전달)
    const campaignPartner = computed(() => ({
      partnerId: campaign.value?.partnerId,
      displayName: campaign.value?.partnerDisplayName,
      introduction: partnerIntro.value
    }))

    const priceRange = computed(() => {
      const types = property.value.types
      if (!types || !types.length) return null
      const prices = types.map(t => t.price).filter(p => p && p > 0)
      if (!prices.length) return null
      const min = Math.min(...prices)
      const max = Math.max(...prices)
      if (min === max) return formatPrice(min)
      return `${formatPrice(min)} ~ ${formatPrice(max)}`
    })

    const getStatusLabel = (s) => ({ upcoming: '분양예정', ongoing: '분양중', completed: '분양완료' }[s] || '-')
    const getPropertyTypeLabel = (t) => ({ apartment: '아파트', officetel: '오피스텔', villa: '빌라', commercial: '상가' }[t] || '-')
    const getThemeLabel = (t) => themeLabels[t] || t
    const getHeatingLabel = (t) => ({ individual: '개별난방', central: '중앙난방', district: '지역난방' }[t] || '-')
    const getGasLabel = (t) => ({ city: '도시가스', individual: '개별난방', lpg: 'LPG' }[t] || '-')

    const formatPrice = (p) => {
      if (!p) return '-'
      if (p >= 10000) {
        const uk = Math.floor(p / 10000)
        const man = p % 10000
        return man ? `${uk}억 ${man.toLocaleString()}만` : `${uk}억`
      }
      return `${p.toLocaleString()}만원`
    }

    const formatMoveInDate = (d) => {
      if (!d) return '-'
      const [y, m] = d.split('-')
      return `${y}년 ${parseInt(m)}월`
    }

    const initMap = async () => {
      if (!mapContainer.value || !property.value.address) return
      if (!window.kakao?.maps) return

      const create = () => {
        if (property.value.coordinates) {
          const coords = new window.kakao.maps.LatLng(property.value.coordinates.lat, property.value.coordinates.lng)
          render(coords)
        } else {
          const gc = new window.kakao.maps.services.Geocoder()
          gc.addressSearch(property.value.address, (res, status) => {
            if (status === window.kakao.maps.services.Status.OK) {
              render(new window.kakao.maps.LatLng(res[0].y, res[0].x))
            }
          })
        }
      }

      const render = (coords) => {
        const map = new window.kakao.maps.Map(mapContainer.value, { center: coords, level: 3 })
        const marker = new window.kakao.maps.Marker({ map, position: coords })
        new window.kakao.maps.InfoWindow({
          content: `<div style="padding:5px 10px;font-size:12px;white-space:nowrap;">${property.value.name}</div>`
        }).open(map, marker)
      }

      window.kakao.maps.LatLng ? create() : window.kakao.maps.load(create)
    }

    const loadData = async () => {
      const code = route.params.code
      if (!code) { loading.value = false; return }

      try {
        campaign.value = await getCampaignByCode(code)
        if (!campaign.value) { loading.value = false; return }

        if (campaign.value.status === 'active') {
          await incrementViews(campaign.value.id)
        }

        const propertySnap = await getDoc(doc(db, 'properties', campaign.value.propertyId))
        if (propertySnap.exists()) {
          property.value = { id: propertySnap.id, ...propertySnap.data() }
          addRecent(propertySnap.id)
        }

        try {
          const q = query(
            collection(db, 'propertyPartners'),
            where('partnerId', '==', campaign.value.partnerId),
            where('propertyId', '==', campaign.value.propertyId)
          )
          const snapshot = await getDocs(q)
          if (!snapshot.empty) {
            const partnerData = snapshot.docs[0].data()
            partnerIntro.value = partnerData.introduction || '정성껏 상담해드리겠습니다.'
          }
        } catch (err) {
          console.error('파트너 정보 로드 실패:', err)
        }

        await loadFavorites()
        if (property.value.id) {
          isFavorited.value = isFavorite(property.value.id)
        }
      } catch (e) {
        console.error('데이터 로드 실패:', e)
      } finally {
        loading.value = false
        await nextTick()
        setTimeout(initMap, 100)
      }
    }

    const goBack = () => router.back()

    const shareProperty = async () => {
      const url = window.location.href
      if (navigator.share) {
        try { await navigator.share({ title: property.value.name, url }) } catch (e) { console.log('공유 취소', e) }
      } else {
        navigator.clipboard.writeText(url)
        alert('링크 복사됨')
      }
    }

    const handleToggleFavorite = async () => {
      if (!property.value.id) return
      await toggleFavorite(property.value.id)
      isFavorited.value = isFavorite(property.value.id)
    }

    const openConsultationForm = () => {
      showConsultationForm.value = true
      document.body.style.overflow = 'hidden'
    }

    const closeConsultationForm = () => {
      showConsultationForm.value = false
      document.body.style.overflow = ''
    }

    const handleSubmitConsultation = async (formData) => {
      try {
        const now = new Date()
        const user = auth.currentUser

        // 광고 유입 여부에 따라 status 결정
        // 광고 유입이면 banner_pending (200P 구매 필요), 아니면 contacted (캠페인 직접 유입)
        const hasAdSource = formData.fromBanner || formData.fromAdBanner ||
          formData.fromPopup || formData.fromSplash ||
          formData.fromRecommendAd || formData.fromCommunityAd || formData.fromChatAd

        const consultationData = {
          customerName: formData.name,
          customerPhone: formData.phone,
          customerId: user?.uid || null,
          customerEmail: user?.email || null,
          propertyId: property.value.id,
          propertyName: property.value.name,
          partnerId: campaign.value.partnerId,
          partnerDisplayName: campaign.value.partnerDisplayName,
          privacyAgreed: formData.privacyAgreed,
          transferAgreed: true, // 캠페인은 24시간 제한 없음
          agreedAt: now.toISOString(),
          // 광고 유입이면 banner_pending, 캠페인 직접 유입이면 contacted
          status: hasAdSource ? 'banner_pending' : 'contacted',
          pickedBy: null,
          pickedByDisplayName: null,
          pickedAt: null,
          memo: '',
          consultStatus: null,
          contactedAt: hasAdSource ? null : now.toISOString(),
          // 캠페인 정보
          fromCampaign: formData.fromCampaign || false,
          campaignId: formData.campaignId || null,
          campaignCode: formData.campaignCode || null,
          // 광고 유입 플래그
          fromJump: false,
          fromBanner: formData.fromBanner || false,
          fromAdBanner: formData.fromAdBanner || false,
          fromPopup: formData.fromPopup || false,
          fromSplash: formData.fromSplash || false,
          fromRecommendAd: formData.fromRecommendAd || false,
          fromCommunityAd: formData.fromCommunityAd || false,
          fromChatAd: formData.fromChatAd || false,
          // 타임스탬프
          createdAt: now.toISOString(),
          expiresAt: null, // 캠페인/광고 유입은 24시간 제한 없음
          updatedAt: now.toISOString()
        }

        await addDoc(collection(db, 'consultationRequests'), consultationData)
        await incrementConsultations(campaign.value.id)

        closeConsultationForm()
        alert('상담 신청이 완료되었습니다!')
        router.push('/')
      } catch (error) {
        console.error('상담 신청 실패:', error)
        alert('상담 신청에 실패했습니다. 다시 시도해주세요.')
      }
    }

    onMounted(loadData)

    return {
      loading, campaign, property, partnerIntro, isFavorited,
      mapContainer, selectedTypeIdx, priceRange, campaignPartner,
      showConsultationForm, adSource,
      getStatusLabel, getPropertyTypeLabel, getThemeLabel,
      getHeatingLabel, getGasLabel, formatPrice, formatMoveInDate,
      goBack, shareProperty, handleToggleFavorite,
      openConsultationForm, closeConsultationForm, handleSubmitConsultation
    }
  }
}
</script>

<style scoped>
.detail-page {
  min-height: 100vh;
  background: #ffffff;
}

/* 헤더 */
.detail-header {
  position: sticky;
  top: 60px; /* 메인헤더(60px) 바로 아래 붙음 */
  z-index: 40;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 12px;
  background: white;
}

@media (max-width: 768px) {
  .detail-header {
    top: 0;
    border-bottom: 1px solid #e2e8f0;
  }
}
.header-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}
.header-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
}
.header-actions {
  display: flex;
  gap: 0;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding-top: 56px;
  color: #94a3b8;
}
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #2563eb;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }
.error-icon {
  width: 80px;
  height: 80px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.error-icon.expired { background: #fef3c7; }
.error-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}
.error-desc {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 20px;
}
.home-btn {
  padding: 12px 24px;
  background: #2563eb;
  color: white;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
}

.content {
  max-width: 480px;
  margin: 0 auto;
}

.campaign-badge-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-bottom: 1px solid #93c5fd;
}
.campaign-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #2563eb;
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
}
.partner-badge {
  font-size: 13px;
  font-weight: 600;
  color: #1d4ed8;
}

.summary-bar {
  background: white;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.summary-address {
  font-size: 13px;
  color: #1e293b;
  margin-bottom: 4px;
  font-weight: 500;
}
.summary-info {
  font-size: 12px;
  color: #475569;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.summary-info .divider { color: #cbd5e1; }

.image-area {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
}
.image-area img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.no-image {
  width: 100%;
  height: 100%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}
.badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 6px;
}
.badge {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
}
.badge-upcoming { background: #fef3c7; color: #d97706; }
.badge-ongoing { background: #d1fae5; color: #059669; }
.badge-completed { background: #e2e8f0; color: #64748b; }

.section {
  background: white;
  padding: 20px 16px;
  margin-bottom: 8px;
}
.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
}

.name {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}
.price-range { margin: 0; }
.range-value {
  font-size: 18px;
  font-weight: 700;
  color: #2563eb;
}

.price-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.price-row .label {
  font-size: 13px;
  color: #64748b;
}
.price-row .value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.price-row .value.primary {
  color: #2563eb;
  font-size: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.info-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}
.info-label {
  font-size: 13px;
  color: #64748b;
}
.info-value {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

.type-section { background: white; }
.type-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 16px;
  -webkit-overflow-scrolling: touch;
}
.type-tabs::-webkit-scrollbar { display: none; }
.type-tab {
  flex-shrink: 0;
  padding: 10px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
}
.type-tab:hover {
  background: #f1f5f9;
  color: #1e293b;
}
.type-tab.active {
  background: #0f172a;
  border-color: #0f172a;
  color: white;
}
.type-detail-card {
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
}
.type-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}
.type-detail-left {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.type-detail-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.type-detail-pyeong {
  font-size: 14px;
  color: #64748b;
}
.type-detail-price {
  font-size: 17px;
  font-weight: 700;
  color: #2563eb;
}
.type-detail-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}
.type-floorplan {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  margin-top: 4px;
  object-fit: cover;
}

.map-box {
  width: 100%;
  height: 180px;
  background: #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.map-addr {
  font-size: 12px;
  color: #64748b;
  margin-top: 10px;
}

.partner-section { background: white; }
.partner-card-main {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 16px;
  margin-bottom: 12px;
}
.partner-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  flex-shrink: 0;
}
.partner-info {
  flex: 1;
  min-width: 0;
}
.partner-name {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: #1d4ed8;
  margin-bottom: 4px;
}
.partner-intro {
  display: block;
  font-size: 13px;
  color: #2563eb;
}

.bottom-space { height: 140px; }
@media (min-width: 769px) {
  .bottom-space {
    height: 80px;
  }
}

/* 하단 바 - 모바일: 플로팅, 웹: 콘텐츠 내 고정 */
.bottom-bar {
  position: fixed;
  bottom: 64px; /* BottomNav 높이 */
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: white;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
}
/* 웹 버전: 하단 고정, 너비 제한, 둥근 모서리 */
@media (min-width: 769px) {
  .bottom-bar {
    bottom: 0;
    max-width: 600px;
    border-radius: 20px 20px 0 0;
  }
}
.bottom-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.price-main {
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
}
.price-sub {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}
.cta-btn {
  padding: 14px 32px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  font-size: 15px;
  font-weight: 700;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
  transition: all 0.2s ease;
}
.cta-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
}
.cta-btn:disabled {
  background: #cbd5e1;
  box-shadow: none;
}
</style>
